/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Directive,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {firstValueFrom, Subscription} from 'rxjs';
import {BatteryPassAttributeLabel} from '../BatteryPassAttributeLabel';
import {BatteryPassSelection} from '../BatteryPassAttributeSelection';
import {User} from '../User';
import {UserManagementService} from '../../services/user-management.service';
import {PassportService} from '../../services/passport.service';
import {TranslateService} from '@ngx-translate/core';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {ActivatedRoute, Router} from '@angular/router';
import {AssignmentService} from '../../services/assignment.service';
import {ErrorPageDetail} from '../../app/components/error/error.component';
import {AppSettings} from '../Constants';
import {SimpleModalComponent} from '../../app/shared/modals/simple-modal/simple-modal.component';
import {ModalCreateChildrenComponent} from '../../app/components/battery/modal-create-children/modal-create-children.component';
import {ModalImportBatteryDataComponent} from '../../app/components/battery/modal-import-battery-data/modal-import-battery-data.component';
import {FormFieldAttribute, FormPurpose} from '../FormCategory';
import {AbstractControl, FormGroup} from '@angular/forms';
import {FeatureFlaggingService} from '../../services/feature-flagging.service';
import {ConnectorService} from '../../services/connector.service';
import {UserService} from '../../services/user.service';
import {SchemaDTO, UISchemaDTO} from '../Schema';
import {
  MasterChildStringEnum,
  PassportDto,
  StatusStringEnum,
} from '../Passport';
import {ModalEditHierarchyComponent} from '../../app/components/battery/modal-edit-hierarchy/modal-edit-hierarchy.component';
import {RemovePassportModalComponent} from '../../app/shared/modals/remove-passport-modal/remove-passport-modal.component';
import {
  getSchemaVersionLabel,
  LONGLIST_I18N_KEY,
} from '../../app/components/battery/schema-version-label.pipe';

@Directive()
export abstract class AbstractFormWrapper implements OnDestroy, OnInit {
  @ViewChild('cancelModal', {read: TemplateRef})
  cancelModalRef!: TemplateRef<HTMLElement>;

  @ViewChild('provideAssetModal', {read: TemplateRef})
  provideAssetModalRef!: TemplateRef<HTMLElement>;

  @ViewChild('changeNameModal', {read: TemplateRef})
  changeNameModalRef!: TemplateRef<HTMLElement>;

  passportStatus?: StatusStringEnum;

  purpose: FormPurpose = 'edit';

  loading = true;
  isLoadingUserData = false;

  selectedCategory = 0;

  allButDataProvider = AppSettings.usedRoles.filter(
    (role) => role !== 'DATA_PROVIDER',
  );

  dataProviders: User[] = [];

  passportId!: string;
  passDisplayName?: string;
  masterChildFlag?: MasterChildStringEnum;
  protected readonly StatusStringEnum = StatusStringEnum;
  isMasterPassport = false;
  schema: SchemaDTO['data'];
  uiSchema?: UISchemaDTO['data'];
  schemaName = '';

  routeSubscription?: Subscription;

  assignmentPaneExpanded = false;
  assignmentPaneData?: BatteryPassAttributeLabel | null;
  attributeSelections?: BatteryPassSelection[] = [];

  initialAttributeLabelData: BatteryPassAttributeLabel[] = [];
  appendableLabelData: BatteryPassAttributeLabel[] = [];

  errorLoadingPassData = false;
  errorDetail: ErrorPageDetail = {
    errorCode: '404',
    errorHeader: 'error.header.not_found',
    errorDescription: 'battery_pass.edit.errors.not_found',
  };

  isHierarchyFeatureEnabled = false;

  // Data provider assignment variables
  cxAssetForm!: FormGroup;
  edcConfigured = false;
  isCatenaXEnabled = false;
  canSwitchProvisionType = false;

  readonly userManagementService = inject(UserManagementService);
  readonly passportService = inject(PassportService);
  readonly translate = inject(TranslateService);
  readonly toastService = inject(ToastService);
  readonly modalService = inject(ModalService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly assignmentService = inject(AssignmentService);
  readonly featureFlaggingService = inject(FeatureFlaggingService);
  readonly connectorService = inject(ConnectorService);
  readonly userService = inject(UserService);
  readonly injector = inject(Injector);

  ngOnInit(): void {
    this.setupEffect();
    this.loading = true;
    this.routeSubscription = this.route.params.subscribe(async (params) => {
      this.passportId = params['id'];

      const featureFlag = await firstValueFrom(
        this.featureFlaggingService.load$(),
      );
      if (featureFlag?.features.cx === true) {
        const orgId = this.userService.userDataChange.value?.organization?.id;
        if (orgId) {
          try {
            const consumerConnectors = await firstValueFrom(
              this.connectorService.load$(orgId, 'consumer'),
            );
            this.isCatenaXEnabled = consumerConnectors.length > 0;
          } catch {
            this.isCatenaXEnabled = false;
          }
        }
      } else {
        this.isCatenaXEnabled = false;
      }

      if (featureFlag.features.passport?.hierarchy) {
        this.isHierarchyFeatureEnabled = true;
      }

      await this.afterNgOnInit();
    });
  }

  setupEffect() {
    runInInjectionContext(this.injector, () => {
      effect(async () => {
        const batteryPass = this.passportService.passes()[this.passportId];
        if (batteryPass) {
          this.passDisplayName = batteryPass?.metadata?.name
            ? `${batteryPass.metadata.name}`
            : this.passportId;
          this.masterChildFlag = batteryPass?.metadata?.masterChildFlag;
          this.isMasterPassport =
            batteryPass?.metadata?.masterChildFlag ===
            MasterChildStringEnum.MASTER;
          this.passportStatus =
            batteryPass?.metadata?.status?.status || StatusStringEnum.DRAFT;
          this.loading = false;
        }
        this.attributeSelections =
          this.assignmentService.attributeSelections()[this.passportId] || [];
        const userList = this.userManagementService.userList();
        if (userList) {
          this.dataProviders =
            await this.userManagementService.getDataProviderList();
          this.isLoadingUserData = false;
        }
      });
    });
  }

  async afterNgOnInit() {
    await Promise.all([this.loadBatteryPass(), this.loadDataProvider()]);
  }

  ngOnDestroy() {
    const passes = this.passportService.passes();
    delete passes[this.passportId];
    this.passportService.passes.set(passes);
    // Reset the global validation-error state so a subsequently opened
    // passport does not inherit stale 422 errors from this one (the
    // BehaviorSubjects on PassportService are app-wide singletons and
    // replay their last value to new subscribers).
    this.passportService.resetValidationErrors();
    if (this.routeSubscription?.unsubscribe) {
      this.routeSubscription?.unsubscribe();
    }
  }

  async loadBatteryPass() {
    this.loading = true;
    try {
      const passport = await this.passportService.loadOne(
        this.passportId,
        true,
      );
      const schemaDTO = await this.loadSchema(
        passport.metadata?.schemaId || '',
      );
      this.schema = schemaDTO.data;
      this.schemaName = `${this.schema.name} (${getSchemaVersionLabel(this.schema, undefined, this.translate.instant(LONGLIST_I18N_KEY))})`;
      try {
        const uiSchemaDTO = await firstValueFrom(
          this.passportService.loadUiSchema$(passport.metadata?.schemaId || ''),
        );
        this.uiSchema = uiSchemaDTO.data;
      } catch {
        // uiSchema is optional — a missing or failing uiSchema does not block passport load
      }
    } catch {
      this.errorLoadingPassData = true;
    }
    this.loading = false;
  }

  async loadSchema(schemaId: string) {
    return this.passportService.loadSchema(schemaId);
  }

  /**
   * Loads user list and attribute assignments concurrently, then enriches
   * each assignment with the assignee's email address.
   *
   * `isLoadingUserData` is always reset to `false` in the `finally` block so
   * that loading indicators clear correctly on both success and error, and on
   * repeated calls where the `userList` signal may not change (which would
   * prevent the reactive effect from firing its own reset).
   */
  async loadDataProvider() {
    this.isLoadingUserData = true;

    try {
      const [, assignments] = await Promise.all([
        firstValueFrom(this.userManagementService.loadUserList$()),
        firstValueFrom(
          this.assignmentService.loadAttributeAssignments$(
            undefined,
            this.passportId,
          ),
        ),
      ]);
      this.initialAttributeLabelData = this.addEmailAddressToAssignment(
        this.dataProviders,
        assignments,
      );
    } finally {
      this.isLoadingUserData = false;
    }
  }

  /**
   * Filters attribute assignments to those with a matching data provider and
   * attaches the provider's email to each retained assignment.
   */
  addEmailAddressToAssignment(
    dataProviders: User[],
    attributeLabelData: BatteryPassAttributeLabel[],
  ) {
    const filteredAttributeLabel: BatteryPassAttributeLabel[] = [];
    attributeLabelData.forEach((attribute) => {
      const user = dataProviders.find(
        (dataProvider) => dataProvider.id === attribute.assigneeId,
      );
      if (user) {
        attribute.assigneeEmail = user.email;
        filteredAttributeLabel.push(attribute);
      }
    });
    return filteredAttributeLabel;
  }

  async onCancelClick() {
    const cachedPass = structuredClone(
      this.passportService.originalPasses[this.passportId],
    );
    if (this.passportService.passportChanged(this.passportId)) {
      const instance = await this.modalService.open({
        content: this.cancelModalRef,
        centered: true,
        data: this.passDisplayName,
      });

      instance.onClose.on(async (shouldCancel) => {
        if (shouldCancel) {
          if (this.passportId && cachedPass) {
            await this.passportService.update(
              this.passportId,
              cachedPass,
              undefined,
            );
          }
          await this.navigateToPassportList();
        }
      });
    } else {
      await this.navigateToPassportList();
    }
  }

  async onChangePassportNameClick() {
    const instance = await this.modalService.open({
      content: this.changeNameModalRef,
      centered: true,
    });

    instance.onClose.on(async (passportName: string) => {
      if (passportName) {
        const passports = this.passportService.passes();
        const passport = passports[this.passportId] || ({} as PassportDto);
        if (passport && passport.metadata) {
          passport.metadata.name = passportName;
          this.passportService.passes.set(passports);
          this.passDisplayName = passportName;
          await this.passportService.update(
            this.passportId,
            passport,
            undefined,
          );
        }
      }
    });
  }

  async onChangeHierarchyClick() {
    const instance = await this.modalService.open({
      size: '600',
      content: ModalEditHierarchyComponent,
      data: {id: this.passportId},
      centered: true,
      closeOnBackdropClick: false,
    });
    return new Promise<boolean | undefined>((resolve) => {
      instance.onClose.on((result: boolean) => {
        if (result) {
          const successText = this.translate.instant(
            'battery_pass.hierarchy.update_successful',
          );
          this.toastService.show({message: successText, type: 'success'});
        }
        resolve(result);
      });
      instance.onDismiss.on(() => resolve(undefined));
    });
  }

  async navigateToPassportList() {
    if (this.purpose === 'assignment') {
      await this.router.navigate(['/app/assign-attributes']);
    } else {
      await this.router.navigate(['/app/list-passports']);
    }
  }

  async onSaveClick(status: StatusStringEnum) {
    await this.showMasterChildMessages(this.purpose);
    try {
      await this.passportService.update(
        this.passportId,
        this.passportService.passportsInEdit[this.passportId] ||
          ({} as PassportDto),
        this.appendableLabelData,
      );
      await this.passportService.updateStatus(this.passportId, status);
      const message = this.translate.instant(
        this.purpose === 'edit'
          ? 'battery_pass.edit.edited_message'
          : 'battery_pass.create.created_message',
      );
      await this.toastService.show({message: message, type: 'success'});

      await this.navigateToPassportList();
    } catch (error: unknown) {
      const updateError = error as {
        error: {message: Array<{field: string; message: string}>};
      };
      let categoryError = false;
      updateError.error?.message?.forEach((errorMessage) => {
        if (errorMessage.field === '') {
          categoryError = true;
        }
      });
      let message = '';
      if (categoryError) {
        message = this.translate.instant(
          'battery_pass.edit.errors.category_empty',
        );
      } else {
        message = this.translate.instant(
          'battery_pass.edit.errors.validation_error',
        );
      }
      const title = this.translate.instant(
        'battery_pass.edit.errors.validation_error_title',
      );
      await this.toastService.show({title, message, type: 'error'});
    }
  }

  async showMasterChildMessages(purpose: string) {
    const passport = this.passportService.passportsInEdit[this.passportId];
    if (
      passport &&
      passport.metadata &&
      passport.metadata.masterChildFlag === MasterChildStringEnum.MASTER &&
      purpose === 'edit'
    ) {
      const result = await this.showSimpleModal(
        await this.translate.instant('general.info'),
        await this.translate.instant(
          'battery_pass.edit.changes_applied_to_children',
        ),
      );
      if (result === undefined) {
        return;
      }
    }
    if (
      passport &&
      passport.metadata &&
      passport.metadata.masterChildFlag === MasterChildStringEnum.MASTER &&
      purpose === 'create'
    ) {
      await this.showCreateChildrenModal();
    }
  }

  async showSimpleModal(title: string, content: string) {
    const instance = await this.modalService.open({
      size: '480',
      content: SimpleModalComponent,
      data: {
        title: title,
        content: content,
      },
      centered: true,
      closeOnBackdropClick: true,
    });
    return new Promise<boolean | undefined>((resolve) => {
      instance.onClose.on((result) => resolve(result));
      instance.onDismiss.on(() => resolve(undefined));
    });
  }

  async showCreateChildrenModal() {
    const instance = await this.modalService.open({
      size: '480',
      content: ModalCreateChildrenComponent,
      data: {id: this.passportId, isTemplateCreationInProgress: true},
      centered: true,
      closeOnBackdropClick: true,
    });
    return new Promise<boolean | undefined>((resolve) => {
      instance.onClose.on((result: boolean) => resolve(result));
      instance.onDismiss.on(() => resolve(undefined));
    });
  }

  expandAssignmentPane(attributeLabel: BatteryPassAttributeLabel) {
    this.assignmentPaneData = attributeLabel;
    this.assignmentPaneExpanded = true;
  }

  onAssignmentPaneChanged(event: CustomEvent) {
    setTimeout(() => {
      this.assignmentPaneExpanded = event.detail.expanded;
    }, 0);
  }

  getAssignmentProviders(): string[] {
    return Array.from(
      new Set(
        this.initialAttributeLabelData
          .filter((assignment) => assignment.status === 'open')
          .map((assignment) => assignment.assigneeEmail),
      ),
    );
  }

  async onImportCXClick() {
    const instance = await this.modalService.open({
      size: '840',
      content: ModalImportBatteryDataComponent,
      centered: true,
      beforeDismiss: async () => {
        await this.loadBatteryPass();
        await this.loadDataProvider();
        return true;
      },
      closeOnBackdropClick: false,
      data: {
        providers: this.getAssignmentProviders(),
        attributeLabelData: this.initialAttributeLabelData,
      },
    });
    instance.onClose.on(async () => {
      this.loading = true;
      setTimeout(async () => {
        await this.loadBatteryPass();
        await this.loadDataProvider();
      }, 0);
    });
  }

  onDataProviderSelected(user: User) {
    if (this.attributeSelections) {
      for (const selection of this.attributeSelections) {
        const index = this.appendableLabelData.findIndex(
          (entry) => entry.attribute === selection.attribute,
        );
        if (index !== -1) {
          // If the entry exists, update it
          this.appendableLabelData[index].assigneeId = user.id;
          this.appendableLabelData[index].assigneeEmail = user.email;
        } else {
          // If the entry doesn't exist, push a new one
          this.appendableLabelData.push({
            attribute: selection.attribute,
            assigneeId: user.id,
            assigneeEmail: user.email,
          });
        }
      }
    }

    // Empty the selections after assignment
    this.attributeSelections = [];
    this.assignmentService.attributeSelections.set({
      ...this.assignmentService.attributeSelections(),
      [this.passportId]: [],
    });
  }

  async updateAssignmentLabelsOnRemove(
    removedAssignment: BatteryPassAttributeLabel,
  ) {
    if (removedAssignment?.id) {
      await this.assignmentService.deleteAttributeAssignment(
        removedAssignment.id,
      );
      this.initialAttributeLabelData = this.initialAttributeLabelData.filter(
        (label) => label !== removedAssignment,
      );
    } else {
      this.appendableLabelData = this.appendableLabelData.filter(
        (label) => label !== removedAssignment,
      );
    }
    this.assignmentPaneExpanded = false;
  }

  // Data-provider assignment methods

  async onSubmitCx() {
    // can be overridden in derived classes to handle specific submit logic
  }

  async onSaveImport() {
    // can be overridden in derived classes to handle specific save logic
  }

  onProvisionTypeCXClick() {
    // can be overridden in derived classes to handle specific logic
  }

  onProvisionTypeAPIClick() {
    // can be overridden in derived classes to handle specific logic
  }

  saveFunction(
    _value: string | number,
    _attribute: FormFieldAttribute,
    _control: AbstractControl,
    _fullValuePath: string,
  ) {
    // can be overridden in derived classes to handle specific save logic
  }

  async onProvideAssetClick() {
    // can be overridden in derived classes to handle specific provide asset logic
  }

  async onRemoveClick() {
    const id = this.passportId;
    if (!id) {
      return;
    }

    const passport = this.passportService.passes()[id];
    const name = passport?.metadata?.name;
    const instance = await this.modalService.open({
      content: RemovePassportModalComponent,
      centered: true,
      data: {
        passport,
        name: name ?? id,
      },
    });

    instance.onClose.on(async (shouldRemove: boolean) => {
      if (shouldRemove) {
        await firstValueFrom(this.passportService.delete$(id));
        this.translate
          .get('battery_pass.list.removing_battery_pass_success')
          .subscribe((msg: string) => {
            this.toastService.show({message: msg, type: 'success'});
          });
        await this.navigateToPassportList();
      }
    });
  }
}
